import {type ReactNode, type ComponentType} from 'react';
import {type BaseFieldProps, type WrappedFieldsProps} from 'redux-form';
import {type css} from 'styled-components';

import {type ElementProps} from 'web-app/react/components/form-elements/containers/element/element';
import {
    type Optional,
    type DistributiveOmit,
    type DistributivePack,
    type DistributiveCopy,
    type DistributiveOptional,
} from 'web-app/util/typescript';
import type * as Time from 'web-app/react/components/form-elements/time/time';
import {type ReduxFormTimePickerProps} from 'web-app/react/components/form-elements/time/timepicker';

import {type InputProps as PeriodPickerProps} from '../form-elements/period-picker/period-picker';
import {type DayPickerInputProps} from '../form-elements/daypicker/day-picker-input';
import {type FormFileInputProps} from '../form-elements/containers/form-file-input';
import {type rangeToString} from '../form-elements/containers/range-picker/helpers';
import {type DateTimePickerValue} from '../form-elements/datetimepicker/types';

export interface GenericFormData {
    fields: GenericItem[];
}

export type GenericItem = GenericFieldSection | GenericFormField | GenericFieldElement | GenericFieldArray;

export type GenericFormField =
    | GenericFieldText
    | GenericFieldTextArea
    | GenericFieldSelect
    | GenericFieldSelectV2
    | GenericFieldRadio
    | GenericFieldCheckbox
    | GenericFieldDatePicker
    | GenericFieldRangePicker
    | GenericFieldPeriodPicker
    | GenericFieldDateTimePicker
    | GenericFieldNewTimePicker
    | GenericFieldFileInput
    | GenericFieldCustom;

type GenericFieldValue = GenericFormField['value'];

interface GenericFieldBase {
    key?: string; // use this for react component keys if you have multiple fields with the same name
    label?: ReactNode;
    onlyRenderIf?: boolean;
    onlyRenderRules?: GenericFieldRenderRule[];
}

export interface GenericField extends GenericFieldBase {
    name: string;
    appearance?: ElementProps['appearance'];
    placeholder?: string | null;
    props?: any;
    propRules?: GenericFieldPropRule[];
    changeRules?: GenericFieldChangeRule[];
    elementCSS?: ReturnType<typeof css>;
    elementProps?: ElementProps;
    validate?: BaseFieldProps['validate'];
    parse?: BaseFieldProps['parse'];
    ['data-e2e-id']?: string;
    ['data-intercom-target']?: string;
}

export interface GenericFieldText extends GenericField {
    fieldType: typeof GenericFieldType.text;
    value?: string | number;
    type?: string;
}

export interface GenericFieldTextArea extends GenericField {
    fieldType: typeof GenericFieldType.textArea;
    value?: string | null;
}

export interface GenericFieldNewTimePicker extends GenericField {
    fieldType: typeof GenericFieldType.newTimepicker;
    value: Time.Time | null;
    props?: ReduxFormTimePickerProps;
}

export interface GenericFieldRangePicker extends GenericField {
    fieldType: typeof GenericFieldType.rangePicker;
    value?: ReturnType<typeof rangeToString>;
}

export interface GenericFieldPeriodPicker extends GenericField {
    fieldType: typeof GenericFieldType.periodPicker;
    props: Optional<PeriodPickerProps, 'onChange'>;
    dateFormat: string;
    value?: {
        from?: string;
        to?: string;
    };
}

export interface GenericFieldDateTimePicker extends GenericField {
    fieldType: typeof GenericFieldType.dateTimePicker;
    value?: DateTimePickerValue;
}

export interface GenericFieldSelect extends GenericField {
    fieldType: typeof GenericFieldType.select | typeof GenericFieldType.listSelect;
    options: any[];
    value?: any;
}

export interface GenericFieldSelectV2 extends GenericField {
    fieldType: typeof GenericFieldType.selectV2;
    options: any[];
    value?: any;
    useOptionAsValue?: boolean;
}

export interface GenericFieldRadio extends GenericField {
    fieldType: typeof GenericFieldType.radio;
    options: any[]; // TODO: convert to select option types types when possible
    value?: string;
}

export interface GenericFieldCheckbox extends GenericField {
    fieldType: typeof GenericFieldType.checkbox;
    text: ReactNode;
    value?: boolean;
    withElement?: boolean; // Use this if you don't use label but still want an Element to wrap
}

export interface GenericFieldFileInput extends GenericField {
    fieldType: typeof GenericFieldType.fileInput;
    props?: FormFileInputProps;
    value?: undefined;
}

export interface GenericFieldDatePicker extends GenericField {
    fieldType: typeof GenericFieldType.dayPicker;
    props?: Omit<DayPickerInputProps, 'value' | 'onChange'> &
        Pick<Optional<DayPickerInputProps, 'onChange'>, 'onChange'>;
    value?: DayPickerInputProps['value'];
}

export interface GenericFieldSection extends GenericFieldBase {
    name?: string;
    fieldType: typeof GenericFieldType.section;
    fields: GenericItem[];
    props?: any;
}

export interface GenericFieldElement extends GenericFieldBase {
    name?: string;
    fieldType: typeof GenericFieldType.element;
    appearance?: ElementProps['appearance'];
    fields: GenericItem[];
    props?: any;
    elementCSS?: ReturnType<typeof css>;
}

/**
 * Transforming GenericFormField to be used for FieldArray:
 * 1. Omit unused fields
 * 2. Copy `value` into `emptyValue`
 * 3. Make `emptyValue` optional
 * 4. Transform `value` to array type
 */
type ArrayFieldStripped<T extends GenericFormField> = DistributiveOmit<
    T,
    'name' | 'label' | 'onlyRenderIf' | 'onlyRenderRules'
>;
type ArrayFieldEmptyValue<T extends GenericFormField> = DistributiveCopy<ArrayFieldStripped<T>, 'value', 'emptyValue'>;
type ArrayFieldOptionalEmptyValue<T extends GenericFormField> = DistributiveOptional<
    ArrayFieldEmptyValue<T>,
    'emptyValue'
>;
type GenericArrayField<T extends GenericFormField> = DistributivePack<ArrayFieldOptionalEmptyValue<T>, 'value'>;

export interface GenericFieldArray extends GenericFieldBase {
    fieldType: typeof GenericFieldType.fieldArray;
    name: string;
    addText?: string;
    required?: boolean;
    /**
     * If data-e2e-id is passed to an array field it will:
     * - Be added to the add button with `${data-e2e-id}-ADD`
     * - Be added to each field with `${data-e2e-id}-${i}`, e.g. `${data-e2e-id}-0, ${data-e2e-id}-1, ...`
     */
    ['data-e2e-id']?: string;
    /**
     * arrayField is any form field in the union GenericFormField...
     * - without properties `name`, `label`, and `onlyRenderIf`
     * - with `value` changed from `value: V` to `value: V[]`
     */
    arrayField:
        | GenericArrayField<Exclude<GenericFormField, {fieldType: typeof GenericFieldType.custom}>>
        | GenericArrayField<
              Omit<GenericFieldCustom, 'component'> & {
                  component: CustomFieldArrayComponent;
              }
          >;
}

export type CustomFieldComponent<P = {}> = ComponentType<
    React.PropsWithChildren<WrappedFieldsProps & {passive?: boolean} & P>
>;
export type CustomFieldArrayComponent<P = {}> = CustomFieldComponent<P & {index: number}>;
export interface GenericFieldCustom<Names extends string = string, P = any>
    extends Omit<GenericField, 'name' | 'value'> {
    fieldType: typeof GenericFieldType.custom;
    name?: undefined;
    /**
     * Values can contain any K/V pair, which will be added to the form values
     * when the form is mounted.
     *
     * Example:
     * ```
     * names: ['foo', 'bar'],
     * values: {foo: 'SomeValue', bar: null, hiddenValue: 'Just showing this is possible'}
     * ```
     */
    names: Names[];
    value?: {[key in Names]?: any} & {[key: string]: any};
    component: CustomFieldComponent<P>;
    props?: P;
}

/**
 * Because the custom field has generic types we need to use a function
 * to have them inferred.
 * - `makeGenericFieldCustom` if you wish to add a normal custom field.
 * - `makeGenericFieldCustomForFieldArray` if you wish to add a custom field to a field array.
 */
export const makeGenericFieldCustom = <Names extends string, P>(props: GenericFieldCustom<Names, P>) => props;
export const makeGenericFieldCustomForFieldArray = <Names extends string, P>(
    props: GenericArrayField<
        Omit<GenericFieldCustom<Names, P>, 'component'> & {
            component: CustomFieldArrayComponent<P>;
        }
    >,
) => props;

interface GenericFieldChangeRuleBase {
    change?: string;
    rule: keyof typeof GenericFieldRule;
    value?: GenericFieldValue | keyof typeof GenericFieldSetTo | ((value?: GenericFieldValue) => GenericFieldValue);
    changePath?: string;
}

export interface GenericFieldChangeRuleWhen extends GenericFieldChangeRuleBase {
    when: GenericFieldValue;
}

export interface GenericFieldChangeRuleWhenValueOf extends GenericFieldChangeRuleBase {
    whenValueOf: string;
}

export interface GenericFieldChangeRuleWhenFunction extends Omit<GenericFieldChangeRuleBase, 'rule'> {
    rule: (value?: GenericFieldValue) => boolean;
}

export type GenericFieldChangeRule =
    | GenericFieldChangeRuleWhen
    | GenericFieldChangeRuleWhenValueOf
    | GenericFieldChangeRuleWhenFunction;

export interface GenericFieldPropRule {
    prop: string;
    name: string;
    rule: keyof typeof GenericFieldRule;
    value?: GenericFieldValue;
    path?: string;
}

export interface GenericFieldRenderRule {
    name: string;
    rule: keyof typeof GenericFieldRule;
    value: GenericFieldValue;
}

export const GenericFieldSetTo = {
    setToThis: 'setToThis',
} as const;

export const GenericFieldRule = {
    greaterThan: 'greaterThan',
    lessThan: 'lessThan',
    notEquals: 'notEquals',
    equals: 'equals',
} as const;

export const GenericFieldType = {
    text: 'text',
    textArea: 'textArea',
    select: 'select',
    selectV2: 'selectV2',
    listSelect: 'listSelect',
    radio: 'radio',
    checkbox: 'checkbox',
    dayPicker: 'dayPicker',
    section: 'section',
    element: 'element',
    newTimepicker: 'newTimepicker',
    rangePicker: 'rangePicker',
    periodPicker: 'periodPicker',
    dateTimePicker: 'dateTimePicker',
    fileInput: 'fileInput',
    fieldArray: 'fieldArray',
    custom: 'custom',
} as const;
